import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAccess } from "@/features/access/AccessProvider";
import { slugForRoutePrefix } from "@/features/access/navSlugs";
import { useAuthStore } from "@/stores/authStore";
import { pickHomeForRole } from "@/app/router/roleHome";

/**
 * Gates a route subtree by the OBJECT REGISTRY — the same `can(slug)` the
 * navigation filters on and the same grants the backend's @Permissions sit
 * behind — instead of by the legacy `User.role` string.
 *
 * Why the string is the wrong gate (BP-ISS-20260917-069, 2026-09-18): the
 * admin block was `RoleGuard allowedRoles={["ADMIN","SUPER_ADMIN"]}`, but the
 * backend writes only STAFF / CLIENT for every login it creates — elevation
 * "goes through role membership, not this string" (users-admin.service.ts).
 * So the literal ADMIN existed on exactly the two original Somibo accounts,
 * and the first tenant admin (Devyakt, ORG_ADMIN with every admin.* grant)
 * clicked Team & Security and was bounced to /home with no message.
 *
 * Resolution is by longest route prefix, so `/lov/:lovId/edit` inherits
 * `/lov`'s object and `/settings/tags` inherits `/settings`'s — detail
 * routes inherit the parent screen's grant (navSlugs' stated rule). An
 * unmapped route fails OPEN, for the reason useNavAccess gives: an unmapped
 * entry is far more likely to be a slug somebody forgot than a screen
 * somebody should lose, and failing open surfaces the gap. Nothing renders
 * until /access/me has answered — deny while loading, no flash of a page
 * the person may not see.
 */
export function ObjectGuard() {
  const { user } = useAuthStore();
  const { can, isReady } = useAccess();
  const { pathname } = useLocation();

  if (!user) return <Navigate to="/login" replace />;
  if (!isReady) return null;

  const slug = slugForRoutePrefix(pathname);
  if (slug && !can(slug)) {
    return <Navigate to={pickHomeForRole(user.role)} replace />;
  }
  return <Outlet />;
}
